import axios from 'axios';

/* Istanza axios dedicata invece del client globale: così la configurazione di
 * Bacheca non si sovrappone a quella di eventuali altri script nella pagina
 * (rilevante quando la SPA gira dentro il wrapper Capacitor/Tauri). */
const client = axios.create({
  baseURL: '/api/v2/',
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  withCredentials: true,
});

/** Legge un cookie per nome. Django mette qui il token CSRF. */
export function readCookie(name) {
  const prefix = `${name}=`;
  for (const chunk of document.cookie.split(';')) {
    const value = chunk.trim();
    if (value.startsWith(prefix)) {
      return decodeURIComponent(value.slice(prefix.length));
    }
  }
  return null;
}

/* axios inietta l'header XSRF da solo, ma solo per richieste same-origin e
 * quando il cookie è già presente. Il middleware ForceCSRFCookieMiddleware
 * lato Django garantisce che lo sia; questo interceptor copre il caso in cui
 * il cookie arrivi dopo il primo render. */
client.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
    const token = readCookie('csrftoken');
    if (token) config.headers['X-CSRFToken'] = token;
  }
  return config;
});

/** Estrae un messaggio leggibile da un errore axios/DRF. */
export function errorMessage(error, fallback = 'Qualcosa è andato storto.') {
  /* 413 lo emette nginx, non Django: il corpo è la sua pagina HTML, che
   * mostrata così com'è non direbbe nulla. Peggio, quando nginx tronca la
   * richiesta il browser non vede alcuna risposta e axios dice solo "Network
   * Error", indistinguibile da una rete caduta. Qui si dà un nome alla cosa. */
  if (error?.response?.status === 413) {
    return 'Richiesta troppo grande per il server: riprova con meno file per volta.';
  }
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  // DRF restituisce {campo: [messaggi]}: si prende il primo utile.
  const first = Object.values(data)[0];
  if (Array.isArray(first)) return String(first[0]);
  if (typeof first === 'string') return first;
  return fallback;
}

/** Errori di validazione per campo, nella forma {campo: "messaggio"}. */
export function fieldErrors(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(data)) {
    out[key] = Array.isArray(value) ? String(value[0]) : String(value);
  }
  return out;
}

export default client;
